/**
 * Prompt Service - Handles API interactions for prompt templates and libraries
 */

import axios from 'axios';
import { get, post, put } from '../utils/apiUtils';

// No need to use BASE_URL as it's already configured in apiUtils.js
const API_URL = `/api/v1/persistence/prompt`;

/**
 * Get all prompts with optional filtering
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of prompts
 */
export const getPrompts = async (filters = {}, sort = {}, query = null) => {
  try {
    const response = await post(`${API_URL}/fetch?${query}`, { filter: filters,sort: sort });
    return response;
  } catch (error) {
    console.error('Error fetching prompts:', error);
    throw error;
  }
};

/**
 * Get a specific prompt by ID
 * @param {string} promptId - Prompt ID
 * @returns {Promise<Object>} Promise resolving to prompt object
 */
export const getPromptById = async (promptId) => {
  try {
    const response = await get(`${API_URL}/${promptId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Create a new prompt
 * @param {Object} promptData - Prompt data
 * @returns {Promise<Object>} Promise resolving to created prompt
 */
export const createPrompt = async (promptData) => {
  try {
    const response = await post(API_URL, promptData);
    return response;
  } catch (error) {
    console.error('Error creating prompt:', error);
    throw error;
  }
};

/**
 * Update an existing prompt
 * @param {string} promptId - Prompt ID
 * @param {Object} promptData - Updated prompt data
 * @returns {Promise<Object>} Promise resolving to updated prompt
 */
export const updatePrompt = async (promptData) => {
  try {
    const response = await put(`${API_URL}`, promptData);
    return response;
  } catch (error) {
    console.error(`Error updating prompt:`, error);
    throw error;
  }
};

/**
 * Delete a prompt
 * @param {string} promptId - Prompt ID
 * @returns {Promise<void>} Promise resolving when prompt is deleted
 */
export const deletePrompt = async (promptId) => {
  try {
    await axios.delete(`${API_URL}/${promptId}`);
  } catch (error) {
    console.error(`Error deleting prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Get prompt variables
 * @param {string} promptId - Prompt ID
 * @returns {Promise<Array>} Promise resolving to array of prompt variables
 */
export const getPromptVariables = async (promptId) => {
  try {
    const response = await axios.get(`${API_URL}/${promptId}/variables`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching variables for prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Update prompt variables
 * @param {string} promptId - Prompt ID
 * @param {Array} variables - Updated variables
 * @returns {Promise<Object>} Promise resolving to updated prompt
 */
export const updatePromptVariables = async (promptId, variables) => {
  try {
    const response = await axios.put(`${API_URL}/${promptId}/variables`, { variables });
    return response.data;
  } catch (error) {
    console.error(`Error updating variables for prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Get prompt examples
 * @param {string} promptId - Prompt ID
 * @returns {Promise<Array>} Promise resolving to array of prompt examples
 */
export const getPromptExamples = async (promptId) => {
  try {
    const response = await axios.get(`${API_URL}/${promptId}/examples`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching examples for prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Add a prompt example
 * @param {string} promptId - Prompt ID
 * @param {Object} example - Example data
 * @returns {Promise<Object>} Promise resolving to created example
 */
export const addPromptExample = async (promptId, example) => {
  try {
    const response = await axios.post(`${API_URL}/${promptId}/examples`, example);
    return response.data;
  } catch (error) {
    console.error(`Error adding example to prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Delete a prompt example
 * @param {string} promptId - Prompt ID
 * @param {string} exampleId - Example ID
 * @returns {Promise<void>} Promise resolving when example is deleted
 */
export const deletePromptExample = async (promptId, exampleId) => {
  try {
    await axios.delete(`${API_URL}/${promptId}/examples/${exampleId}`);
  } catch (error) {
    console.error(`Error deleting example ${exampleId} from prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Execute a prompt with provided variables
 * @param {string} promptId - Prompt ID
 * @param {Object} variables - Variables to use in the prompt
 * @returns {Promise<Object>} Promise resolving to execution result
 */
export const executePrompt = async (promptId, variables) => {
  try {
    const response = await axios.post(`${API_URL}/${promptId}/execute`, { variables });
    return response.data;
  } catch (error) {
    console.error(`Error executing prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Get prompt usage statistics
 * @param {string} promptId - Prompt ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} Promise resolving to usage statistics
 */
export const getPromptUsage = async (promptId, filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/${promptId}/usage`, { params: filters });
    return response.data;
  } catch (error) {
    console.error(`Error fetching usage for prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Get prompt categories
 * @returns {Promise<Array>} Promise resolving to array of prompt categories
 */
export const getPromptCategories = async () => {
  try {
    const response = await axios.get(`${API_URL}/categories`);
    return response.data;
  } catch (error) {
    console.error('Error fetching prompt categories:', error);
    throw error;
  }
};

/**
 * Create a new prompt category
 * @param {Object} categoryData - Category data
 * @returns {Promise<Object>} Promise resolving to created category
 */
export const createPromptCategory = async (categoryData) => {
  try {
    const response = await axios.post(`${API_URL}/categories`, categoryData);
    return response.data;
  } catch (error) {
    console.error('Error creating prompt category:', error);
    throw error;
  }
};

/**
 * Get prompt feedback
 * @param {string} promptId - Prompt ID
 * @returns {Promise<Array>} Promise resolving to array of feedback
 */
export const getPromptFeedback = async (promptId) => {
  try {
    const response = await axios.get(`${API_URL}/${promptId}/feedback`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching feedback for prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Add feedback for a prompt
 * @param {string} promptId - Prompt ID
 * @param {Object} feedback - Feedback data
 * @returns {Promise<Object>} Promise resolving to created feedback
 */
export const addPromptFeedback = async (promptId, feedback) => {
  try {
    const response = await axios.post(`${API_URL}/${promptId}/feedback`, feedback);
    return response.data;
  } catch (error) {
    console.error(`Error adding feedback to prompt ${promptId}:`, error);
    throw error;
  }
};

/**
 * Add feedback for a prompt
 * @param {string} promptId - Prompt ID
 * @param {Object} feedback - Feedback data
 * @returns {Promise<Object>} Promise resolving to created feedback
 */

export const getPromptCount = async (filter = {}) => {
   try {
     const response = await post(`${API_URL}/count`, filter);
     return response.count;
   } catch (error) {
     console.error('Error fetching knowledge bases:', error);
     throw error;
   }
};

export default {
  getPrompts,
  getPromptById,
  createPrompt,
  updatePrompt,
  deletePrompt,
  getPromptVariables,
  updatePromptVariables,
  getPromptExamples,
  addPromptExample,
  deletePromptExample,
  executePrompt,
  getPromptUsage,
  getPromptCategories,
  createPromptCategory,
  getPromptFeedback,
  addPromptFeedback
};

